import { Alert, Button, Col, Form, Modal, Row, message } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { calculateExamResult } from '../../../../services/exam';
import NormalForm from './NormalForm';

const positiveAsthmaResult = {
  procedureCode: 'RESULTADO_ASMA_2_ENCUESTA_SINTOMAS_RESPIRATORIOS',
  value: 'Positivo',
};

const positiveBronchitisResult = {
  procedureCode: 'RESULTADO_BRONQUITIS_4_ENCUESTA_SINTOMAS_RESPIRATORIOS',
  value: 'Positivo',
};

const resultAlertsByFormType = {
  respiratory_symptoms: [
    {
      conditions: [positiveAsthmaResult, positiveBronchitisResult],
      translationKey: 'examPage.questionCalculator.alerts.asthmaAndBronchitisPositive',
    },
    {
      conditions: [positiveAsthmaResult],
      translationKey: 'examPage.questionCalculator.alerts.asthmaPositive',
    },
    {
      conditions: [positiveBronchitisResult],
      translationKey: 'examPage.questionCalculator.alerts.bronchitisPositive',
    },
  ],
};

const resultColumns = 2;

const toAnswer = (value) => (Array.isArray(value) ? value.join(',') : value ?? '');

const hasResult = (procedure) => procedure?.result !== undefined
  && procedure?.result !== null
  && procedure.result.toString().trim() !== '';

const toProcedureFields = (procedures) => procedures
  .map(procedure => ({
    name: ['procedures', procedure.id.toString()],
    value: hasResult(procedure) ? procedure.result : undefined,
  }));

const QuestionCalculatorForm = ({
  inputs,
  form,
  admissionPatientExamId,
  disableForm,
  from,
  ...normalFormProps
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const isConsultation = from === 'consultation';

  const resultAlerts = (resultAlertsByFormType[inputs.form_type] ?? [])
    .map(resultAlert => ({
      ...resultAlert,
      conditions: resultAlert.conditions.map(condition => ({
        ...condition,
        procedure: inputs.procedures?.find(procedure => procedure.code === condition.procedureCode),
      })),
    }))
    .filter(resultAlert => resultAlert.conditions.every(condition => condition.procedure));

  const findResultAlert = (resultOf) => resultAlerts
    .find(({ conditions }) => conditions.every(({ procedure, value }) => resultOf(procedure) === value));

  const showResultAlertModal = ({ translationKey }) => Modal.warning({
    title: t(`${translationKey}.title`),
    content: t(`${translationKey}.description`),
    okText: t('examPage.questionCalculator.alerts.confirm'),
    centered: true,
    width: 500,
  });

  useEffect(() => {
    form.setFields(toProcedureFields(inputs.procedures ?? []));
  }, [form, inputs.procedures]);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const questionValues = form.getFieldsValue(true)?.questions ?? {};
      const response = await calculateExamResult({
        admissionPatientExamId,
        body: {
          form_type: inputs.form_type,
          procedures: (inputs.procedures ?? []).map(procedure => ({
            id: procedure.id,
            procedure_order: procedure.order,
          })),
          questions: (inputs.questions ?? []).map(question => ({
            id: question.id,
            code: question.code,
            survey_code: question.survey_code,
            questionOptions: question.options,
            answer: toAnswer(questionValues[question.id]),
          })),
        },
      });

      const calculatedProcedures = response ?? [];
      form.setFields(toProcedureFields(calculatedProcedures));

      const calculatedResultAlert = findResultAlert(procedure => calculatedProcedures
        .find(calculatedProcedure => calculatedProcedure.id === procedure.id)?.result);
      if (calculatedResultAlert) showResultAlertModal(calculatedResultAlert);
    } catch (error) {
      console.error(error);
      message.error(t('examPage.questionCalculator.error'));
    } finally {
      setLoading(false);
    }
  };

  const sharedNormalFormProps = {
    ...normalFormProps,
    form,
    disableForm,
    admissionPatientExamId,
  };

  const results = (
    <Row>
      <Col lg={24} xs={24} className="col-results">
        <Row justify="center" gutter={[0, 25]}>
          <Col lg={22} xs={22}>
            <h2>{t('examPage.questionCalculator.results')}</h2>
          </Col>
          {resultAlerts.length > 0 && (
            <Form.Item shouldUpdate noStyle>
              {() => {
                const formResultAlert = findResultAlert(procedure => form
                  .getFieldValue(['procedures', procedure.id.toString()]));
                return formResultAlert ? (
                  <Col lg={22} xs={22}>
                    <Alert
                      className="question-calculator-alert"
                      type="error"
                      showIcon
                      icon={<span className="icon-alert-triangle-bold" />}
                      message={<strong>{t(`${formResultAlert.translationKey}.title`)}</strong>}
                    />
                  </Col>
                ) : null;
              }}
            </Form.Item>
          )}
          <Col lg={22} xs={22}>
            <Row justify="end">
              <Col lg={15} xs={24}>
                <Button
                  className="cmt-button primary"
                  type="primary"
                  block
                  onClick={handleCalculate}
                  disabled={disableForm}
                  loading={loading}
                >
                  {t('examPage.questionCalculator.calculate')}
                </Button>
              </Col>
            </Row>
          </Col>
          <Col lg={22} xs={22}>
            <NormalForm
              {...sharedNormalFormProps}
              inputs={{
                ...inputs,
                form_layout: { ...inputs.form_layout, columns: resultColumns },
                questions: [],
                surveys: [],
                measurements: [],
              }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );

  return (
    <Row justify="space-between">
      {isConsultation && (
        <Col lg={24} xs={24} className="mb-lg-10 mb-md-10 mb-sm-10 mb-xs-10">
          {results}
        </Col>
      )}
      <Col lg={isConsultation ? 24 : 14} xs={24} className="col-questions">
        <NormalForm
          {...sharedNormalFormProps}
          inputs={{ ...inputs, procedures: [] }}
        />
      </Col>
      {!isConsultation && (
        <Col lg={9} xs={24} className="mt-lg-0 mt-md-30 mt-sm-30 mt-xs-30">
          {results}
        </Col>
      )}
    </Row>
  );
};

export default QuestionCalculatorForm;
